import React, { FC, useState } from 'react';
import { Button, DatePickerRange, FlexGroup, Popover, SelectItem } from '@lithos';
import { Duration } from 'moment';
import moment from 'moment';

export interface TimeComboOption {
  label: string;
  duration?: Duration;
}

export type TimeComboOptionType =
  | 'custom'
  | '5Minutes'
  | '15Minutes'
  | '30Minutes'
  | '1Hour'
  | '6Hours'
  | '12Hours'
  | '1Day'
  | '2Days'
  | '3Days'
  | '1Week'
  | '2Weeks'
  | '1Month'
  | '2Months'
  | '6Months';

export const TimeComboOptions: Record<TimeComboOptionType, TimeComboOption> = {
  custom: { label: 'Custom' },
  '5Minutes': { label: 'Past 5 minutes', duration: moment.duration(5, 'minutes') },
  '15Minutes': { label: 'Past 15 minutes', duration: moment.duration(15, 'minutes') },
  '30Minutes': { label: 'Past 30 minutes', duration: moment.duration(30, 'minutes') },
  '1Hour': { label: 'Past hour', duration: moment.duration(1, 'hours') },
  '6Hours': { label: 'Past 6 hours', duration: moment.duration(6, 'hours') },
  '12Hours': { label: 'Past 12 hours', duration: moment.duration(12, 'hours') },
  '1Day': { label: 'Past 24 hours', duration: moment.duration(1, 'days') },
  '2Days': { label: 'Past 2 days', duration: moment.duration(2, 'days') },
  '3Days': { label: 'Past 3 days', duration: moment.duration(3, 'days') },
  '1Week': { label: 'Past week', duration: moment.duration(1, 'weeks') },
  '2Weeks': { label: 'Past two weeks', duration: moment.duration(2, 'weeks') },
  '1Month': { label: 'Past month', duration: moment.duration(1, 'months') },
  '2Months': { label: 'Past 2 months', duration: moment.duration(2, 'months') },
  '6Months': { label: 'Past 6 months', duration: moment.duration(6, 'months') },
};

const CustomTimeSelector: FC<{
  start: Date;
  end: Date;
  setStart: (date: Date) => void;
  setEnd: (date: Date) => void;
  handleCustomDateRangeChange: (start: Date, end: Date) => void;
}> = ({ start, end, setStart, setEnd, handleCustomDateRangeChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <SelectItem
      name={
        <>
          Custom
          <Popover
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            trigger={<></>}
            body={
              <FlexGroup direction="column" gapSize="4">
                <DatePickerRange
                  start={start}
                  minLabel="Start"
                  maxLabel="End"
                  end={end}
                  setStart={(start) => {
                    setStart(start as Date);
                  }}
                  setEnd={(end) => {
                    setEnd(end as Date);
                  }}
                  allowTimeSelection={true}
                />
                <Button
                  label="Set Date Range"
                  type="primary"
                  onClick={() => {
                    setIsOpen(false);
                    handleCustomDateRangeChange(start, end);
                  }}
                />
              </FlexGroup>
            }
            maxWidth={600}
          />
        </>
      }
      icon="Calendar"
      onClick={() => {
        setIsOpen(true);
      }}
    />
  );
};

const DefinedTimeSelector: FC<{
  option: TimeComboOptionType;
  setStart: (date: Date) => void;
  setEnd: (date: Date) => void;
  handleSelectedOptionChange: (option: TimeComboOptionType) => void;
}> = ({ option, handleSelectedOptionChange }) => {
  return (
    <SelectItem
      name={<>{TimeComboOptions[option].label}</>}
      onClick={() => {
        handleSelectedOptionChange(option);
      }}
    />
  );
};

const TimeSelector: FC<{ onSelection: (start: Date, end: Date) => void; defaultValue?: TimeComboOptionType }> = ({
  onSelection,
  defaultValue = '1Hour',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<TimeComboOptionType>(defaultValue);
  const [start, setStart] = useState<Date>(() => {
    return moment(new Date()).subtract(1, 'hour').toDate();
  });
  const [end, setEnd] = useState<Date>(() => {
    return new Date();
  });

  const handleSelectedOptionChange = (option: TimeComboOptionType) => {
    // Calculate start and end based on the option here
    const duration = TimeComboOptions[option].duration;
    const start = moment().subtract(duration);
    const end = moment();
    onSelection(start.toDate(), end.toDate());
    setSelectedOption(option);
    setIsOpen(false);
  };

  const handleCustomDateRangeChange = (start: Date, end: Date) => {
    setSelectedOption('custom');
    setIsOpen(false);
    onSelection(start, end);
  };

  return (
    <Popover
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      trigger={
        <Button
          label={TimeComboOptions[selectedOption].label}
          type="secondary"
          iconLeft="Calendar"
          iconRight="ChevronDown"
        />
      }
      placement="bottom-start"
      offsetSize={0}
      body={
        <FlexGroup alignItems="flex-start" justifyContent="flex-start" padding="0" direction="column">
          <>
            {Object.keys(TimeComboOptions).map((key) => {
              if (key === 'custom') {
                return (
                  <CustomTimeSelector
                    start={start}
                    end={end}
                    setStart={setStart}
                    setEnd={setEnd}
                    handleCustomDateRangeChange={handleCustomDateRangeChange}
                  />
                );
              }

              return (
                <DefinedTimeSelector
                  option={key as TimeComboOptionType}
                  setStart={setStart}
                  setEnd={setEnd}
                  handleSelectedOptionChange={handleSelectedOptionChange}
                />
              );
            })}
          </>
        </FlexGroup>
      }
    />
  );
};

export default TimeSelector;
